// Loss analysis tile — FE-SCRM-058 (BE-SCRM-026).
//
// Three sub-views per spec:
//
// 1. Loss-reasons distribution — bar chart over LostReason counts.
// 2. Loss rate by reason and producer — matrix table.
// 3. Competitor win/loss — losses TO each named broker + wins
//    AGAINST each named broker when we displaced via BOR.

import { Card, Table, TBody, THead, Th, Td, Tr } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { useLossAnalysisQuery } from "./hooks";
import { LossReasonsChart } from "./LossReasonsChart";
import {
  LOST_REASON_LABEL, PERIOD_LABEL, formatPercent,
} from "./labels";
import type {
  LossAnalysisResponse,
  ReportPeriod,
} from "./types";


export interface LossAnalysisTileProps {
  period: ReportPeriod;
}


export function LossAnalysisTile({ period }: LossAnalysisTileProps) {
  const query = useLossAnalysisQuery(period);

  return (
    <Card
      data-testid="loss-analysis-tile"
      data-section="loss-analysis"
      className="flex flex-col gap-3"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-md font-semibold text-text-primary">
            Loss analysis
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            Where pursuits fall out + which competitors are taking
            them.
          </p>
        </div>
        <span className="rounded-full bg-recessed px-2 py-0.5 text-[10px] uppercase tracking-wide text-text-tertiary">
          {PERIOD_LABEL[period]}
        </span>
      </header>

      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={3} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load loss analysis"
          retry={() => query.refetch()}
        />
      ) : query.data ? (
        <LossBody data={query.data} />
      ) : null}
    </Card>
  );
}


function LossBody({ data }: { data: LossAnalysisResponse }) {
  const empty = (
    Object.keys(data.loss_reasons_distribution).length === 0
    && Object.keys(data.loss_rate_by_reason_and_producer).length === 0
    && data.competitor_win_loss.length === 0
  );
  if (empty) {
    return (
      <div data-testid="loss-empty-state">
        <DashboardEmptyState
          title="Not enough closed opportunities yet"
          description={
            "Come back after the next round of closes — once pursuits "
            + "land in won/lost, the reason breakdown and the competitor "
            + "tally will appear here."
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {Object.keys(data.loss_reasons_distribution).length > 0 && (
        <section>
          <p className="mb-1.5 text-xs font-medium text-text-secondary">
            Loss reasons
          </p>
          <LossReasonsChart distribution={data.loss_reasons_distribution} />
        </section>
      )}

      <ProducerMatrix breakdown={data.loss_rate_by_reason_and_producer} />

      <CompetitorWinLossList rows={data.competitor_win_loss} />
    </div>
  );
}


function ProducerMatrix({
  breakdown,
}: { breakdown: LossAnalysisResponse["loss_rate_by_reason_and_producer"] }) {
  const entries = Object.entries(breakdown);
  if (entries.length === 0) {
    return (
      <div className="rounded-md bg-recessed p-3 text-xs text-text-secondary">
        No closed opportunities by producer in this window.
      </div>
    );
  }

  // Union of reason codes seen across all producers, ordered by
  // total occurrences desc so the busiest column is leftmost.
  const reasonTotals: Record<string, number> = {};
  for (const [, b] of entries) {
    for (const [r, n] of Object.entries(b.by_reason)) {
      reasonTotals[r] = (reasonTotals[r] ?? 0) + n;
    }
  }
  const reasons = Object.keys(reasonTotals).sort(
    (a, b) => reasonTotals[b] - reasonTotals[a],
  );

  return (
    <div
      data-testid="loss-by-producer-table"
      className="overflow-hidden rounded-md border border-border-subtle"
    >
      <div className="bg-recessed px-2 py-1.5 text-xs font-medium text-text-secondary">
        Loss rate by producer
      </div>
      <Table density="xs" cellPadding="tight">
        <THead className="text-text-tertiary">
          <Tr>
            <Th>Producer</Th>
            <Th>Closes</Th>
            <Th>Loss rate</Th>
            {reasons.map((r) => (
              <Th key={r}>{LOST_REASON_LABEL[r] ?? r}</Th>
            ))}
          </Tr>
        </THead>
        <TBody>
          {entries.map(([producerId, b]) => (
            <Tr
              key={producerId}
              data-testid={`loss-producer-row-${producerId}`}
              className="border-t border-border-subtle"
            >
              <Td className="whitespace-nowrap text-text-primary">
                {b.producer_name || producerId.slice(0, 8)}
              </Td>
              <Td className="text-text-secondary">
                {b.total_closed}
              </Td>
              <Td className="text-text-secondary">
                {formatPercent(b.loss_rate)}
              </Td>
              {reasons.map((r) => (
                <Td key={r} className="text-text-secondary">
                  {b.by_reason[r] ?? 0}
                </Td>
              ))}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}


function CompetitorWinLossList({
  rows,
}: { rows: LossAnalysisResponse["competitor_win_loss"] }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md bg-recessed p-3 text-xs text-text-secondary">
        No named competitors on closed opportunities yet.
      </div>
    );
  }
  return (
    <div
      data-testid="loss-competitor-table"
      className="overflow-hidden rounded-md border border-border-subtle"
    >
      <div className="bg-recessed px-2 py-1.5 text-xs font-medium text-text-secondary">
        Competitor win/loss (against named broker)
      </div>
      <Table density="xs" cellPadding="tight">
        <THead className="text-text-tertiary">
          <Tr>
            <Th>Broker</Th>
            <Th>Lost to</Th>
            <Th>Won against</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr
              key={row.competitor}
              data-testid={`loss-competitor-row-${row.competitor}`}
              className="border-t border-border-subtle"
            >
              <Td className="text-text-primary">
                {row.competitor}
              </Td>
              <Td className="text-text-secondary">
                {row.losses_against}
              </Td>
              <Td className="text-text-secondary">
                {row.wins_against}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
